import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { loginExpenses, clearAuthError } from '../../redux/slices/expensesSlice';
import { Input, Button, Alert } from '../common/index.js';

const ExpensesLoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authLoading, authError } = useSelector((state) => state.expenses);

  const [form, setForm] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.username.trim()) next.username = 'Username is required';
    if (!form.password.trim()) next.password = 'Password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await dispatch(loginExpenses({ username: form.username, password: form.password })).unwrap();
      // Admin (operations) workspace — the daily flow starts at counter sales
      // Land on My Day — what is left today, not a blank entry form
      navigate('/ops/today');
    } catch (_) {}
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
      {authError && <Alert type="error" message={authError} />}

      <Input
        label="Username"
        type="text"
        name="username"
        placeholder="Enter admin username"
        value={form.username}
        onChange={handleChange}
        error={errors.username}
        required
        disabled={authLoading}
      />

      <div className="w-full">
        <label htmlFor="exp-password" className="block text-sm font-semibold text-slate-700 mb-2">
          Password <span className="text-rose-500">*</span>
        </label>
        <div className="relative">
          <input
            id="exp-password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter expenses password"
            value={form.password}
            onChange={handleChange}
            disabled={authLoading}
            autoComplete="new-password"
            className={`w-full px-4 py-3 pr-12 border rounded-xl text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 ${
              errors.password
                ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
            } ${authLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            disabled={authLoading}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
          </button>
        </div>
        {errors.password && <p className="text-rose-500 text-xs mt-1.5 font-medium">{errors.password}</p>}
      </div>

      <Button type="submit" variant="primary" size="lg" loading={authLoading} className="w-full">
        Sign In
      </Button>
    </form>
  );
};

export default ExpensesLoginForm;
